import React, { useState, useEffect, useRef } from 'react';
import { ResponsiveImage } from '../../components';
import css from './ProductImageGallery.module.css';
import seeMoreIcon from '../../assets/images/see-more-icon.svg';

const ProductImageGallery = props => {
  const { listing } = props;
  const [expanded, setExpanded] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Mobile carousel state
  const [carouselIndex, setCarouselIndex] = useState(0);
  const touchStartX = useRef(null);

  const images = listing.images || [];

  // Keyboard nav for lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = e => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex(i => Math.min(i + 1, images.length - 1));
      if (e.key === 'ArrowLeft') setLightboxIndex(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, images.length]);

  if (!images.length) {
    return (
      <div className={css.noImages}>
        <p>No images available</p>
      </div>
    );
  }

  const openLightbox = index => setLightboxIndex(index);

  // ─── Mobile swipe handlers ───
  const handleTouchStart = e => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = e => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) setCarouselIndex(i => Math.min(i + 1, images.length - 1));
      else setCarouselIndex(i => Math.max(i - 1, 0));
    }
    touchStartX.current = null;
  };

  // ─── Desktop grid vars ───
  const mainImage = images[0];
  const mainVariants = Object.keys(mainImage?.attributes?.variants || {}).filter(k =>
    k.startsWith('scaled')
  );
  const gridThumbs = expanded ? images.slice(1) : images.slice(1, 5);
  const hasMoreImages = !expanded && images.length > 5;

  const carouselImage = images[carouselIndex];
  const carouselVariants = Object.keys(carouselImage?.attributes?.variants || {}).filter(k =>
    k.startsWith('scaled')
  );

  return (
    <div className={css.root}>

      {/* ─── Mobile carousel (hidden on desktop) ─── */}
      <div
        className={css.mobileCarousel}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={() => openLightbox(carouselIndex)}
      >
        <ResponsiveImage
          rootClassName={css.carouselImage}
          alt={listing.attributes?.title || ''}
          image={carouselImage}
          variants={carouselVariants}
        />
        {images.length > 1 && (
          <div className={css.carouselDots}>
            {images.map((_, i) => (
              <span
                key={i}
                className={i === carouselIndex ? `${css.dot} ${css.dotActive}` : css.dot}
                onClick={e => { e.stopPropagation(); setCarouselIndex(i); }}
              />
            ))}
          </div>
        )}
      </div>

      {/* ─── Desktop grid (hidden on mobile) ─── */}
      <div className={css.desktopGrid}>
        {/* Main image */}
        <div className={css.mainImageWrapper} onClick={() => openLightbox(0)}>
          <ResponsiveImage
            rootClassName={css.mainImage}
            alt={listing.attributes?.title || ''}
            image={mainImage}
            variants={mainVariants}
          />
        </div>

        {/* Thumbnail grid */}
        {images.length > 1 && (
          <div className={css.thumbnailGrid}>
            {gridThumbs.map((img, i) => {
              const actualIdx = i + 1;
              const thumbVariants = Object.keys(img?.attributes?.variants || {}).filter(k =>
                k.startsWith('scaled')
              );
              return (
                <div
                  key={actualIdx}
                  className={css.thumbnailCell}
                  onClick={() => openLightbox(actualIdx)}
                >
                  <ResponsiveImage
                    rootClassName={css.thumbnailImage}
                    alt={`Image ${actualIdx + 1}`}
                    image={img}
                    variants={thumbVariants}
                  />
                </div>
              );
            })}

            {hasMoreImages && (
              <div className={css.seeMoreButton} onClick={() => setExpanded(true)}>
                <span className={css.seeMoreText}>See more photos</span>
                <img src={seeMoreIcon} alt="" className={css.seeMoreIcon} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Lightbox (both) ─── */}
      {lightboxIndex !== null && (
        <div className={css.lightboxOverlay} onClick={() => setLightboxIndex(null)}>
          <button className={css.lightboxClose} onClick={() => setLightboxIndex(null)}>✕</button>

          {lightboxIndex > 0 && (
            <button
              className={`${css.lightboxNav} ${css.lightboxPrev}`}
              onClick={e => { e.stopPropagation(); setLightboxIndex(i => i - 1); }}
            >
              ‹
            </button>
          )}

          <div className={css.lightboxContent} onClick={e => e.stopPropagation()}>
            <ResponsiveImage
              rootClassName={css.lightboxImage}
              alt={`Image ${lightboxIndex + 1}`}
              image={images[lightboxIndex]}
              variants={Object.keys(images[lightboxIndex]?.attributes?.variants || {}).filter(k =>
                k.startsWith('scaled')
              )}
            />
          </div>

          {lightboxIndex < images.length - 1 && (
            <button
              className={`${css.lightboxNav} ${css.lightboxNext}`}
              onClick={e => { e.stopPropagation(); setLightboxIndex(i => i + 1); }}
            >
              ›
            </button>
          )}

          <div className={css.lightboxCounter}>
            {lightboxIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductImageGallery;
